import { writable } from 'svelte/store';
import * as moment from 'moment';
import {
  EVENT_STATE_INIT,
  EVENT_STATE_PRE,
  EVENT_STATE_LIVE,
  EVENT_STATE_POST,
  EVENT_STATE_ERROR,
  EVENT_STATE_NOT_FOUND,
  EVENT_STATE_STATE_TIMEOUT_WINDOW,
  PLAYER_VIEW_STANDARD,
  PLAYER_STATE_PENDING,
  PLAYER_STATE_SOUND_ON,
  ERROR_CRITICAL,
  STREAM_TYPE_VOD,
  STREAM_STATUS_UNKNOWN,
  STREAM_STATUS_UNAVAILABLE_EARLY,
  STREAM_STATUS_UNAVAILABLE_EXPIRED,
  STREAM_STATUS_ACTIVE,
  CHAT_GENERAL_CHANNEL_NAME,
  HLS_STATE_LOADING,
} from './constants.js';

// Information about the event meta data from the server
function createEventState() {
  const emptyState = (state) => {
    return {
      description: '',
      description_active: '',
      description_post: '',
      description_pre: '',
      embedded_pages: [],
      enable_private_rooms: null,
      enable_subtitles: null,
      enable_vod_conversion: null,
      end: '',
      google_analytics_id: '',
      last_active_time: null,
      logo: '',
      player_hide_countdown: false,
      player_msg_idle: '',
      player_msg_post: '',
      player_msg_pre: '',
      privacy_link: '#',
      source: '',
      start: '',
      state: state || EVENT_STATE_INIT,
      support_link: '#',
      terms_link: '#',
      tip_jar_buttons: '',
      tip_jar_title: '',
      title: '',
      venue: '',
    };
  };

  const { subscribe, set, update } = writable(emptyState());

  let setLive = () => {
    update((state) => {
      state.state = EVENT_STATE_LIVE;
      return state;
    });
  };

  return {
    subscribe,
    set: function(event) {
      try {
        clearTimeout(this.stateTimeout);
      } catch (e) {}

      const date = moment(event.start_time);
      const startDiff = moment().diff(date);

      let state = EVENT_STATE_LIVE;
      if (startDiff < 0) {
        state = EVENT_STATE_PRE;
        if (startDiff > -1 * EVENT_STATE_STATE_TIMEOUT_WINDOW) {
          this.stateTimeout = setTimeout(setLive, -1 * startDiff);
        }
      }
      if (moment().diff(event.end_time) > 0) {
        state = EVENT_STATE_POST;
      }
      set({
        description: event.description,
        description_active: event.description_active,
        description_post: event.description_post,
        description_pre: event.description_pre,
        embedded_pages: event.embedded_pages,
        enable_private_rooms: event.enable_private_rooms,
        enable_subtitles: event.enable_subtitles,
        enable_vod_conversion: event.enable_vod_conversion,
        end: event.end_time,
        google_analytics_id: event.google_analytics_id,
        last_active_time: event.last_active_time,
        logo: event.logo,
        player_hide_countdown: !!event.player_hide_countdown,
        player_msg_idle: event.player_msg_idle,
        player_msg_post: event.player_msg_post,
        player_msg_pre: event.player_msg_pre,
        privacy_link: event.privacy_link,
        source: event.source,
        start: event.start_time,
        state,
        support_link: event.support_link,
        terms_link: event.terms_link,
        tip_jar_buttons: event.tip_jar_buttons,
        tip_jar_title: event.tip_jar_title,
        title: event.title,
        venue: event.venue,
      });
    },
    set404: () => {
      set(emptyState(EVENT_STATE_NOT_FOUND));
    },
    setError: () => {
      update((state) => {
        if (state && state.state && state.state !== EVENT_STATE_INIT) {
          // fall back to the previously cached value
          return state;
        }
        // otherwise, we have nothing so try again later.
        return emptyState(EVENT_STATE_NOT_FOUND);
      });
    },
  };
}

function createHlsState() {
  const { subscribe, set } = writable({ err: HLS_STATE_LOADING });
  return {
    subscribe,
    set: (err) => {
      set({ err });
    },
  };
}

// Information about the stream data from the server
function createStreamState() {
  const { subscribe, set } = writable({
    url: '',
    provider: '',
    type: '',
    state: STREAM_STATUS_UNKNOWN,
    drm: false,
    unrestricted: false,
    event_id: '',
    organizer_id: '',
    external_source_type: '',
    forced_status: null,
    use_chat: false,
    use_reactions: false,
    use_rooms: false,
    show_iframe_embed: false,
    source: '',
    rtmp: '', // only set when authenticated
    key: '', // only set when authenticated
    vod: {
      viewing_start: '',
      viewing_end: '',
    },
    status_message: '',
    settings: {},
  });

  return {
    subscribe,
    set: (stream) => {
      // Availability windows for VOD
      let state = stream.forced_status || stream.status;
      let now = moment();
      if (stream.vod && stream.vod.viewing_start && !stream.vod.viewing_end) {
        stream.vod.viewing_end = '2038-01-01T00:00:00Z';
      } else if (stream.vod && !stream.vod.viewing_start && stream.vod.viewing_end) {
        stream.vod.viewing_start = '2000-01-01T00:00:00Z';
      }
      if (stream.type === STREAM_TYPE_VOD) {
        state = STREAM_STATUS_ACTIVE;
        if (stream.vod.viewing_start && stream.vod.viewing_end) {
          if (now.diff(moment(stream.vod.viewing_start)) < 0) {
            state = STREAM_STATUS_UNAVAILABLE_EARLY;
          } else if (now.diff(moment(stream.vod.viewing_end)) > 0) {
            state = STREAM_STATUS_UNAVAILABLE_EXPIRED;
          }
        }
      }

      set({
        url: stream.url,
        provider: stream.provider,
        type: stream.type,
        state: state,
        drm: !!stream.use_drm,
        unrestricted: stream.unrestricted,
        event_id: stream.event_id,
        organizer_id: stream.organizer_id,
        external_source_type: stream.settings && stream.settings.external_type,
        forced_status: stream.forced_status,
        use_chat: !!stream.use_chat,
        use_rooms: !!stream.use_rooms,
        use_reactions: !!stream.use_reactions,
        show_iframe_embed: !!stream.show_iframe_embed,
        source: stream.source,
        rtmp: stream.rtmp, // only set when authenticated
        key: stream.stream_key, // only set when authenticated
        settings: stream.settings,
        vod: {
          viewing_start: stream.vod.viewing_start,
          viewing_end: stream.vod.viewing_end,
        },
      });
    },
  };
}

// Data needed to implement DRM
function createDRMState() {
  const { subscribe, update } = writable({
    token: '',
    challenge: '',
    valid: true,
  });
  return {
    subscribe,
    setChallenge: (challenge) =>
      update((state) => {
        state.challenge = challenge;
        return state;
      }),
    setToken: (token) =>
      update((state) => {
        state.token = token;
        return state;
      }),
    setValid: (valid) =>
      update((state) => {
        state.valid = valid;
        return state;
      }),
  };
}

// Data needed to implement DRM
function createBannedState() {
  const { subscribe, update } = writable({
    banned: false,
  });
  return {
    subscribe,
    setBanned: (banned) =>
      update((state) => {
        state.banned = banned;
        return state;
      }),
  };
}

function createChatState() {
  const { subscribe, update } = writable({
    history: {},
    reactions: [],
    name: null,
    maxHistoryLength: 100,
    pinned: {
      id: '',
      message: '',
      time: null,
    },
    error: {
      time: null,
      message: null,
    },
  });

  const transformMessage = (message) => {
    try {
      if (!(message && message.time && message.payload && message.payload.message)) {
        console.warn('[state] Received malformed chat payload', message);
        return false;
      }
      const d = Date.parse(message.time);
      if (!d) {
        console.warn('[state] Received malformed chat datetime', message.time);
        return false;
      }
      const payload = {
        message: message.payload.message,
        name: message.payload.name,
        time: new Date(d),
        sub: message.sub,
        moderator: message.payload.moderator,
        hide_chat: !!message.payload.hide_chat,
        id: message.id,
      };
      return payload;
    } catch (e) {
      return false;
    }
  };

  return {
    subscribe,
    setError: (message) =>
      update((state) => {
        state.error = {
          message,
          time: new Date(),
        };
        return state;
      }),
    appendReaction: (message) =>
      update((state) => {
        const max = 25;
        state.reactions.push(message);
        if (state.reactions.length >= max) {
          state.reactions = state.reactions.slice(-1 * max);
        }
        return state;
      }),
    setMaxHistory: (length) =>
      update((state) => {
        state.maxHistoryLength = length;
        return state;
      }),
    setName: (name) =>
      update((state) => {
        state.name = name;
        return state;
      }),
    pinMessage: (id, message, time) =>
      update((state) => {
        state.pinned = {
          id,
          message,
          time,
        };
        return state;
      }),
    loadHistory: (messages, room) => {
      update((state) => {
        room = room || CHAT_GENERAL_CHANNEL_NAME;
        state.history[room] = state.history[room] || [];

        let transformed = messages
          .map((message) => {
            return {
              message: message.message,
              name: message.name,
              time: new Date(Date.parse(message.time)),
              sub: message.subject,
              moderator: message.moderator,
              id: message.id,
            };
          })
          .concat(state.history[room]);

        transformed.sort((a, b) => (a.time < b.time ? -1 : a.time != b.time ? 1 : 0));
        // dedupe
        state.history[room] = transformed
          .map((message, index) => {
            if (index === transformed.length - 1) return message;
            const next = transformed[index + 1];
            return next.id === message.id || !message.id ? false : message;
          })
          .filter((m) => m !== false);
        if (state.history[room].length >= (state.maxHistoryLength || 1000)) {
          state.history[room] = state.history[room].slice(-1 * state.maxHistoryLength);
        }
        return state;
      });
    },
    appendMessage: (message, room) => {
      const payload = transformMessage(message);

      if (!payload) {
        return;
      }

      update((state) => {
        room = room || CHAT_GENERAL_CHANNEL_NAME;
        state.history[room] = state.history[room] || [];
        state.history[room].push(payload);
        if (state.history[room].length >= (state.maxHistoryLength || 1000)) {
          state.history[room] = state.history[room].slice(-1 * state.maxHistoryLength);
        }
        if (message.payload.pin) {
          state.pinned = {
            id: payload.id,
            message: payload.message,
            time: payload.time,
          };
        }
        return state;
      });
    },
    deleteMessage: (message, room) => {
      room = room || CHAT_GENERAL_CHANNEL_NAME;
      if (!(message && message.payload.message_id)) {
        console.warn('[state] Received malformed chat delete payload', message);
        return;
      }

      update((state) => {
        state.history[room] = state.history[room] || [];
        state.history[room] = state.history[room].filter(
          (obj) => obj.id !== message.payload.message_id
        );
        if (state.pinned.id === message.payload.message_id) {
          state.pinned = {
            id: '',
            message: '',
            time: null,
          };
        }
        return state;
      });
    },
  };
}

// Local information about the state of the video player
// at any point in time.
function createPlayerState() {
  const { subscribe, update } = writable({
    state: PLAYER_STATE_PENDING, // state is the play/pause state of the player
    status: PLAYER_STATE_PENDING, // status is used for analytics reporting, has more values than state
    controls: false,
    loaded: false,
    ended: false,
    waiting: true,
    view: PLAYER_VIEW_STANDARD,
    sound: PLAYER_STATE_SOUND_ON,
    command: null,
    commandPayload: null,
    position: 0,
  });
  return {
    subscribe,
    setState: (playerState) =>
      update((state) => {
        state.state = playerState;
        return state;
      }),
    setStatus: (playerStatus) =>
      update((state) => {
        state.status = playerStatus;
        return state;
      }),
    setSoundState: (soundState) =>
      update((state) => {
        state.sound = soundState;
        return state;
      }),
    setPosition: (position) =>
      update((state) => {
        state.position = position;
        return state;
      }),
    setCommandRequest: (command, payload = null) =>
      update((state) => {
        state.command = command;
        state.commandPayload = payload;
        return state;
      }),
    clearCommandRequest: () =>
      update((state) => {
        state.command = null;
        state.commandPayload = null;
        return state;
      }),
    setControls: (controls) =>
      update((state) => {
        state.controls = controls;
        return state;
      }),
    setLoaded: (loaded) =>
      update((state) => {
        state.loaded = loaded;
        return state;
      }),
    setEnded: (ended) =>
      update((state) => {
        state.ended = ended;
        return state;
      }),
    setWaiting: (waiting) =>
      update((state) => {
        state.waiting = waiting;
        return state;
      }),
    setView: (view) =>
      update((state) => {
        state.view = view;
        return state;
      }),
    setDelayStatus: (status) =>
      update((state) => {
        state.delayStatus = status;
        return state;
      }),
  };
}

// Access code state
function createAccessState() {
  return writable({
    token: null,
    code: null,
    success: false,
    error: null,
    eventId: null,
    source: null,
    sub: null,
    // used: null, // time of first use
    // purchased: null, // time of purchase / registration
  });
}

// Error state
function createErrorState() {
  return writable({
    error: '',
    type: ERROR_CRITICAL,
  });
}

// Theme state
function createThemeState() {
  const { subscribe, update } = writable({
    stylesheet: '',
    loaded: false,
  });

  return {
    subscribe,
    setTheme: (theme) =>
      update((state) => {
        return Object.assign(state, theme);
      }),
    setStyleSheet: (stylesheet) =>
      update((state) => {
        state.stylesheet = stylesheet;
        return state;
      }),
    setLoaded: (loaded) =>
      update((state) => {
        state.loaded = loaded;
        return state;
      }),
  };
}

// Broadcaster State
function createBroadcasterState() {
  const { subscribe, set, update } = writable({
    receiver: null,
    settings: {},
  });
  return {
    subscribe,
    setReceiver: (receiver) => {
      set({ receiver });
    },
    setSettings: (settings) => {
      update((state) => {
        state.settings = settings;
        return state;
      });
    },
    addListener: (room, topic, handler) => {
      update((state) => {
        if (!state.receiver) {
          console.warn('[state] Attempting to add a listener before a reciever is defined.');
          return state;
        }
        state.receiver.subscribe(room, topic, handler);
        return state;
      });
    },
    removeListener: (room, topic) => {
      update((state) => {
        if (!state.receiver) {
          console.warn('[state] Attempting to remove a listener before a reciever is defined.');
          return state;
        }
        state.receiver.unsubscribe(room, topic);
        return state;
      });
    },
  };
}

// Viewing Rooms
function createRoomsState() {
  const { subscribe, update } = writable({
    current: null,
    rooms: {
      // <roomId>: {
      //  room_id: '',
      //  room_name: '',
      //  source: '',
      //  event_id: '',
      //  public: 0
      // }
    },
  });

  return {
    subscribe,
    setDefaultRoom: () =>
      update((state) => {
        state.current = null;
        return state;
      }),
    setCurrentRoom: (roomId) =>
      update((state) => {
        if (!(roomId in state.rooms)) {
          return state;
        }
        state.current = state.rooms[roomId];
        return state;
      }),
    removeRoom: (roomId) =>
      update((state) => {
        if (state.currrent && state.current.room_id === roomId) {
          state.current = null;
        }
        delete state.rooms[roomId];
        return state;
      }),
    addRoom: (room) =>
      update((state) => {
        state.rooms[room.room_id] = room;
        if (state.currrent && state.current.room_id === room.room_id) {
          state.current = state.rooms[room.room_id];
        }
        return state;
      }),
  };
}

// OrganizerUser State
function createOrganizerUserState() {
  const { subscribe, update } = writable({
    error: null,
    viewerToken: null,
  });

  return {
    subscribe,
    setError: (error) =>
      update((state) => {
        state.error = error;
        return state;
      }),
    setViewerToken: (token) =>
      update((state) => {
        state.viewerToken = token;
        return state;
      }),
  };
}

// OrganizerUser State
function createAnalyticsState() {
  const { subscribe, update } = writable({
    metrics: {},
  });

  return {
    subscribe,
    setMetric: (metric, data) =>
      update((state) => {
        state.metrics[metric] = data;
        return state;
      }),
  };
}

// BannedUsers State
function createBannedUsersState() {
  const { subscribe, update } = writable({
    bannedAccess: {},
    bannedChat: {},
  });

  return {
    subscribe,
    setBannedChatUsers: (users) =>
      update((state) => {
        state.bannedChat = users;
        return state;
      }),
    addBannedChatUser: (sub, name) =>
      update((state) => {
        state.bannedChat[sub] = `{"name": "${name}"}`;
        return state;
      }),
    removeBannedChatUser: (sub) =>
      update((state) => {
        delete state.bannedChat[sub];
        return state;
      }),
    setBannedAccessUsers: (users) =>
      update((state) => {
        state.bannedAccess = users;
        return state;
      }),
  };
}

// RulesState
function createRulesState() {
  const { subscribe, update } = writable({
    rules: [],
  });

  return {
    subscribe,
    set: (rules) =>
      update((state) => {
        state.rules = rules.rules;
        return state;
      }),
    add: (rule) =>
      update((state) => {
        state.rules.push(rule);
        return state;
      }),
    remove: (rule) =>
      update((state) => {
        let newRules = state.rules;
        let removeIndex = newRules.findIndex((x) => x.id === rule);
        if (removeIndex > -1) {
          newRules.splice(removeIndex, 1);
        }
        state.rules = newRules;
        return state;
      }),
  };
}

// StreamAssetsState
function createStreamAssetsState() {
  const { subscribe, update } = writable({
    assets: new Map(),
  });

  return {
    subscribe,
    setAssets: (assets) =>
      update((state) => {
        state.assets.clear();
        assets.forEach(a => {
          state.assets.set(a.id, a)
        });
        return state;
      }),
    setAsset: (key, value) =>
      update((state) => {
        state.assets.set(key, value);
        return state;
      }),
  };
}

// MuxEventLogState
function createMuxLogState() {
  const { subscribe, update } = writable({
    logs: [],
  });

  return {
    subscribe,
    setLogs: (logs) =>
      update((state) => {
        state.logs = logs;
        return state;
      }),
  };
}

export const errorState = createErrorState();
export const accessState = createAccessState();
export const eventState = createEventState();
export const streamState = createStreamState();
export const hlsState = createHlsState();
export const playerState = createPlayerState();
export const themeState = createThemeState();
export const drmState = createDRMState();
export const bannedState = createBannedState();
export const chatState = createChatState();
export const broadcasterState = createBroadcasterState();
export const orgUserState = createOrganizerUserState();
export const analyticsState = createAnalyticsState();
export const roomsState = createRoomsState();
export const bannedUsersState = createBannedUsersState();
export const rulesState = createRulesState();
export const streamAssetsState = createStreamAssetsState();
export const muxLogState = createMuxLogState();
